// src/components/Layout/Sidebar.tsx
import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Collapse,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import CategoryIcon from "@mui/icons-material/Category";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import RadioIcon from "@mui/icons-material/Radio";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SupportIcon from "@mui/icons-material/Support";
import ViewListIcon from "@mui/icons-material/ViewList";

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const drawerWidth = 260;

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const location = useLocation();
  const theme = useTheme();
  const [podcastSubmenuOpen, setPodcastSubmenuOpen] = useState(false);
  const [newsSubmenuOpen, setNewsSubmenuOpen] = useState(false);

  // Check current route for active state
  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  // Effect to open submenus if we're on related pages
  useEffect(() => {
    if (location.pathname.includes("/podcast")) {
      setPodcastSubmenuOpen(true);
    }
    if (location.pathname.includes("/news")) {
      setNewsSubmenuOpen(true);
    }
  }, [location.pathname]);

  const handlePodcastClick = () => {
    setPodcastSubmenuOpen(!podcastSubmenuOpen);
  };

  const handleNewsClick = () => {
    setNewsSubmenuOpen(!newsSubmenuOpen);
  };

  const menuItems = [
    // { text: "Dashboard", icon: <HomeIcon />, path: "/" },
    {
      text: "News",
      icon: <NewspaperIcon />,
      submenu: [
        {
          text: "All News",
          icon: <NewspaperIcon fontSize="small" />,
          path: "/news",
        },
        {
          text: "Categories",
          icon: <CategoryIcon fontSize="small" />,
          path: "/news/categories",
        },
        {
          text: "Grouped View",
          icon: <ViewListIcon fontSize="small" />,
          path: "/news/grouped",
        },
      ],
    },
    {
      text: "Podcasts",
      icon: <PodcastsIcon />,
      submenu: [
        {
          text: "All Podcasts",
          icon: <RadioIcon fontSize="small" />,
          path: "/podcasts",
        },
        {
          text: "Podcast Types",
          icon: <CategoryIcon fontSize="small" />,
          path: "/podcast-types",
        },
      ],
    },
    { text: "Settings", icon: <SettingsIcon />, path: "/settings" },
  ];

  return (
    <Drawer
      variant="persistent"
      open={open}
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Toolbar />

      <Box
        sx={{
          overflow: "auto",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <List sx={{ px: 1 }}>
          {menuItems.map((item) =>
            item.submenu ? (
              <React.Fragment key={item.text}>
                <ListItemButton
                  onClick={
                    item.text === "News" ? handleNewsClick : handlePodcastClick
                  }
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    backgroundColor: isActive(
                      item.text === "News" ? "/news" : "/podcast"
                    )
                      ? theme.palette.action.selected
                      : "transparent",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                  {(
                    item.text === "News" ? newsSubmenuOpen : podcastSubmenuOpen
                  ) ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </ListItemButton>
                <Collapse
                  in={
                    item.text === "News" ? newsSubmenuOpen : podcastSubmenuOpen
                  }
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.submenu.map((subitem) => (
                      <ListItemButton
                        key={subitem.text}
                        component={Link}
                        to={subitem.path}
                        sx={{
                          pl: 4,
                          borderRadius: 2,
                          mb: 0.5,
                          backgroundColor: isActive(subitem.path)
                            ? theme.palette.action.selected
                            : "transparent",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {subitem.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={subitem.text}
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: isActive(subitem.path) ? 600 : 400,
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ) : (
              <ListItemButton
                key={item.text}
                component={Link}
                to={item.path}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  backgroundColor: isActive(item.path)
                    ? theme.palette.action.selected
                    : "transparent",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 600 : 400,
                  }}
                />
              </ListItemButton>
            )
          )}
        </List>

        <Box sx={{ mt: "auto", mb: 2 }}>
          <Divider sx={{ my: 2 }} />
          {/* <List sx={{ px: 1 }}>
            <ListItemButton
              sx={{
                borderRadius: 2,
                mb: 0.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <HelpOutlineIcon />
              </ListItemIcon>
              <ListItemText primary="Help & Documentation" />
            </ListItemButton>
            <ListItemButton
              sx={{
                borderRadius: 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <SupportIcon />
              </ListItemIcon>
              <ListItemText primary="Support" />
            </ListItemButton>
          </List> */}

          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Radio Admin v0.1.0
            </Typography>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
